import { css } from "@emotion/react";
import React from "react";
import useD3 from "../util/use-d3";

import {
    axisBottom,
    axisLeft,
    min,
    max,
    line,
    extent,
    scaleLinear,
    timeDay,
    timeFormat,
    pointer,
    bisector,
    format,
    scaleTime,
    ticks,
    timeMonth,
    timeMonths,
    curveStep,
} from "d3";
import { DateTime } from "luxon";

const dayFormat = timeFormat("%b %d");
const monthFormat = timeFormat("%b %Y");
const tipDateFormat = timeFormat(); //"%B %Y";
const tipNumberFormat = format(",");
export default function DailyRankChart({
    data,
    rankIndex = "rank",
    dateIndex = "date",
    rankLabel = "Rank",
    width = "100%",
    height = "15vh",
    margin = "1rem 0",
    yTicks = 6,
    noborder,
}) {
    const [elemRef, dimensionRef] = useD3(
        (svg, { width, height }) => {
            if (!data) return;

            const margin = {
                top: noborder ? 15 : 25,
                right: noborder ? 5 : 25,
                bottom: 25,
                left: noborder ? 5 : 25,
            };

            const w = width - margin.left - margin.right,
                h = height - margin.top - margin.bottom;

            const chartArea = svg.select(".chart-area");
            chartArea.attr(
                "transform",
                `translate(${margin.left},${margin.top})`
            );

            const xScale = scaleTime()
                .domain(extent(data, (d) => new Date(d[dateIndex])))
                .range([0, w]);
            const xAxis = axisBottom(xScale);
            // ticks every month, but skip labels proportionally to the number to data points
            //.ticks(timeMonth)
            //.tickFormat((d, i) => (i % Math.ceil(data.length / 16) == 0 ? monthFormat(d) : ""));
            svg.select(".x-axis").attr(
                "transform",
                `translate(${margin.left},${margin.top + h})`
            );

            const yScale = scaleLinear()
                .domain([
                    max(data, (d) => d[rankIndex]) + 1,
                    min(data, (d) => d[rankIndex]) - 1,
                ])
                .range([h, 0])
                .nice();
            const yAxis = axisLeft(yScale).ticks(yTicks, "~s");
            svg.select(".y-axis").attr(
                "transform",
                `translate(${margin.left},${margin.top})`
            );

            const rankLine = line()
                .x((d) => xScale(new Date(d[dateIndex])))
                .y((d) => yScale(d[rankIndex]))
                .curve(curveStep);

            const [x1, x2] = xScale.range();
            svg.select(".grid")
                .selectAll("line.grid-y")
                .data(yScale.ticks(yTicks))
                .join("line")
                .attr("class", "grid-y")
                .attr("x1", x1)
                .attr("y1", yScale)
                .attr("x2", x2)
                .attr("y2", yScale)
                .attr("stroke", "#eee");

            //const [y1, y2] = yScale.range();
            //svg
            //    .select(".grid")
            //    .selectAll("line.grid-x")
            //    .data(xScale.ticks(data.length))
            //    .join("line")
            //    .attr("class", "grid-x")
            //    .attr("x1", xScale)
            //    .attr("y1", y1)
            //    .attr("x2", xScale)
            //    .attr("y2", y2)
            //    .attr("stroke", d => (new Date(d).getDay() == 1 ? "#eee" : "none"));

            chartArea
                .selectAll(".rank-line")
                .datum(data)
                .join("path")
                .attr("class", "rank-line")
                .attr("fill", "none")
                .attr("stroke", "#3690b4")
                .attr("stroke-width", 4)
                .attr("stroke-opacity", 0.85)
                .attr("d", rankLine);

            const pointerX = svg
                .select(".pointerX")
                .attr("stroke", "#eee")
                .attr("stroke-dasharray", "4, 2")
                .attr("d", `M 0 0 V ${h}`)
                .style("display", "none");
            const pointerY = svg
                .select(".pointerY")
                .attr("stroke", "#eee")
                .attr("stroke-dasharray", "4, 2")
                .attr("d", `M 0 0 H ${w}`)
                .style("display", "none");

            const ow = 235,
                oh = 48;
            const toolTip = chartArea
                .select(".toolTip")
                .style("display", "none");
            const tipDate = toolTip.select(".date");
            const tipRankLabel = toolTip.select(".rank-label").text(rankLabel);
            const tipRank = toolTip.select(".rank");
            //const tipPrevious = toolTip.select(".previous");
            toolTip.select("rect").attr("width", ow).attr("height", oh);

            svg.select(".x-axis").call(xAxis);
            //svg.select(".y-axis").call(yAxis);

            function mouseout() {
                pointerX.style("display", "none");
                pointerY.style("display", "none");
                toolTip.style("display", "none");
                //setState(state => ({ ...state, tipVisible: false }));
            }

            function mouseover() {}

            const bisectDate = bisector((d) => new Date(d[dateIndex])).left;

            function mousemove(event) {
                const p = pointer(event),
                    x0 = xScale.invert(p[0]),
                    i = bisectDate(data, x0),
                    d0 = data[i - 1],
                    d1 = data[i];

                //console.log({ p, x0, i, d0, d1 });

                if (d0 && d1) {
                    // because curveStep draws its area centered
                    // on the date axis we need to pick the date
                    // closest to the mouse instead of the date
                    // we are over
                    const d =
                        x0 - new Date(d0[dateIndex]) >
                        new Date(d1[dateIndex]) - x0
                            ? d1
                            : d0;

                    //setState(state => ({ ...state, date: d[0], downloads: d[1] }));
                    const xPos = p[0], //xScale(new Date(d[0])),
                        yPos = p[1],
                        xDot = xScale(new Date(d[dateIndex])),
                        yDot = yScale(d[rankIndex]),
                        xTip = Math.max(0, Math.min(xPos - ow / 2, w - ow)),
                        yTip = Math.max(0, Math.min(yPos - oh, h - oh));
                    tipDate.text(d[dateIndex].substring(0, 10));
                    tipRank.text(tipNumberFormat(d[rankIndex]));
                    toolTip
                        .attr("transform", `translate(${xTip}, ${yTip})`)
                        .style("display", null);

                    pointerX
                        .attr("transform", `translate(${xPos}, 0)`)
                        .style("display", null);
                    pointerY
                        .attr("transform", `translate(0, ${yPos})`)
                        .style("display", null);
                } else {
                    mouseout();
                }
            }

            const mouseRect = svg
                .select(".mouse")
                .attr("fill", "none")
                .style("pointer-events", "all")
                .attr("width", w)
                .attr("height", h)
                .attr("transform", `translate(${margin.left},${margin.top})`)
                .on("mouseout", mouseout)
                .on("mouseover", mouseover)
                .on("mousemove", mousemove);
        },
        [data, rankIndex, rankLabel, width, height, noborder]
    );
    return (
        <div
            ref={dimensionRef}
            style={{
                margin,
            }}
        >
            <svg
                ref={elemRef}
                css={css`
                    border: 1px solid ${noborder ? "transparent" : "#eee"};
                    display: block;
                    width: ${width};
                    height: ${height};
                    cursor: crosshair;

                    & .axis {
                        color: #777;
                        shape-rendering: crispEdges;
                    }
                    & .toolTip {
                        .date {
                            font-size: 12px;
                        }
                        .current {
                            font-size: 12px;
                        }
                        .previous {
                            font-size: 12px;
                        }
                        text {
                            fill: black;
                            font-size: 12px;
                        }
                        rect {
                            opacity: 0.9;
                            fill: white;
                            stroke: #eee;
                        }
                    }
                `}
            >
                <g className="chart-area">
                    <g className="grid" />
                    <path className="rank-line" />
                    <path className="pointerX" />
                    <path className="pointerY" />
                    <g className="toolTip" style={{ display: "none" }}>
                        <rect />
                        <text
                            className="rank-label"
                            x="22"
                            y="20"
                            fontWeight="bold"
                        />
                        <line
                            x1="10"
                            y1="32"
                            x2="18"
                            y2="32"
                            stroke="#3690b4"
                            strokeWidth="8"
                        />
                        <text className="date" x="22" y="36" />
                        <text
                            textAnchor="end"
                            dx="220"
                            y="36"
                            className="rank"
                        />
                    </g>
                </g>
                <g className="axis y-axis" />
                <g className="axis x-axis" />
                <rect className="mouse" />
            </svg>
        </div>
    );
}
